import { useEffect } from 'react';

// Sets/creates a <meta> tag and returns a restore function.
function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', value);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute('content', prev);
  };
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute('href', prev);
  };
}

/**
 * Per-route SEO: sets document title, description, canonical, Open Graph / Twitter
 * tags, and injects JSON-LD structured data. Restores everything on unmount so
 * SPA navigation back to other routes keeps their metadata correct.
 */
export function useSeo({ title, description, canonical, image, jsonLd = [] }) {
  useEffect(() => {
    const restores = [];
    const prevTitle = document.title;
    if (title) {
      document.title = title;
      restores.push(setMeta('property', 'og:title', title));
      restores.push(setMeta('name', 'twitter:title', title));
    }
    if (description) {
      restores.push(setMeta('name', 'description', description));
      restores.push(setMeta('property', 'og:description', description));
      restores.push(setMeta('name', 'twitter:description', description));
    }
    if (canonical) {
      restores.push(setCanonical(canonical));
      restores.push(setMeta('property', 'og:url', canonical));
    }
    if (image) {
      restores.push(setMeta('property', 'og:image', image));
      restores.push(setMeta('name', 'twitter:image', image));
    }
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const scripts = blocks.filter(Boolean).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', 'route');
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => {
      document.title = prevTitle;
      restores.forEach((fn) => fn());
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
